import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';


export type AdminUserSegment =
  | 'all' | 'unconfirmed' | 'no_patient' | 'no_plan' | 'no_shift' | 'inactive_14' | 'active' | 'free' | 'paying';

export interface AdminUserRow {
  user_id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  last_activity_at: string | null;
  days_inactive: number | null;
  account_type: string;
  company_name: string | null;
  email_confirmed?: boolean;
  last_email_status?: string | null;
  last_email_template?: string | null;
  last_email_at?: string | null;
  subscription_status: string | null;
  personal_subscription_status?: string | null;
  personal_subscription_env?: string | null;
  company_subscription_status?: string | null;
  company_subscription_env?: string | null;
  patients_count: number;
  patients_with_plan: number;
  shifts_count: number;
  executions_period: number;
  last_outreach_at?: string | null;
  last_outreach_step?: string | null;
  last_outreach_clicked?: boolean;
  blocked_step?: string | null;
  days_stuck?: number | null;
}

export interface OutreachRecord {
  id: string;
  funnel_step: string;
  custom_message: string | null;
  sent_by_name: string | null;
  status: string;
  error_message: string | null;
  clicked_at: string | null;
  click_count: number;
  created_at: string;
}


export interface AdminUserSearchData {
  generated_at: string;
  period_days: number;
  total: number;
  counts: Record<AdminUserSegment, number>;
  users: AdminUserRow[];
}

export interface FunnelStep {
  key: string;
  done: boolean;
  at: string | null;
}

export interface AdminSubscriptionInfo {
  status: string;
  interval: string | null;
  unit_amount: number | null;
  currency: string | null;
  quantity: number | null;
  patient_slots: number | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  canceled_at: string | null;
  payment_failed_at: string | null;
  stripe_env: string | null;
  is_company: boolean;
}

export interface GrowthOpportunity {
  key: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  suggestion: string;
}

export interface AdminUser360Data {
  generated_at: string;
  period_days: number;
  identity: {
    user_id: string;
    email: string | null;
    full_name: string | null;
    avatar_url: string | null;
    phone: string | null;
    created_at: string;
    account_age_days: number;
    last_sign_in_at: string | null;
    email_confirmed: boolean;
    providers: string[];
    account_type: string;
    roles: string[];
    company: { id: string; name: string; role: string; is_owner: boolean } | null;
    referred_by_code: string | null;
  };
  subscription: AdminSubscriptionInfo | null;
  personal_subscription?: AdminSubscriptionInfo | null;
  company_subscription?: (AdminSubscriptionInfo & { company_name?: string | null }) | null;
  health: {
    score: number;
    status: 'healthy' | 'at_risk' | 'cooling' | 'dormant';
    last_activity_at: string | null;
    days_inactive: number | null;
    components: Record<string, number>;
  };
  funnel: { steps: FunnelStep[]; blocked_step: string | null; days_stuck: number | null };
  usage: {
    patients_total: number;
    patients_active: number;
    patients_archived: number;
    patients_suspended: number;
    care_plan_tasks_active: number;
    care_plan_by_type: Record<string, number>;
    shift_templates: number;
    shifts_period: number;
    shifts_period_user: number;
    shifts_open: number;
    avg_shift_hours: number | null;
    executions_period: number;
    executions_period_user: number;
    planned_period: number;
    adherence_pct: number | null;
    late_executions: number;
    late_executions_user: number;
    late_pct: number | null;
    incidents: number;
    sporadic_meds: number;
    vital_alerts: number;
    activity_series: Array<{ date: string; shifts: number; executions: number; shifts_user: number; executions_user: number }>;

  };
  team: {
    members_active: number;
    caregivers: number;
    observers: number;
    invites_sent: number;
    invites_accepted: number;
    invites_expired: number;
  };
  communication: {
    notifications_created: number;
    notifications_read: number;
    push_ok: number;
    push_failed: number;
    emails_sent: number;
    emails_failed: number;
    marketing_opt_in: boolean | null;
    marketing_unsubscribed_at: string | null;
    marketing_eligible: boolean;
  };
  patients: Array<{
    patient_id: string;
    nickname: string;
    status: string;
    created_at: string;
    is_owner: boolean;
    plan_tasks: number;
    templates: number;
    shifts_period: number;
    shifts_period_user: number;
    executions_period: number;
    executions_period_user: number;

    planned_period: number;
    adherence_pct: number | null;
    last_activity_at: string | null;
  }>;
  opportunities: GrowthOpportunity[];
  outreach?: OutreachRecord[];
  outreach_stats?: { sent: number; clicked: number; last_sent_at: string | null };
  timeline: Array<{ at: string; type: string; label: string }>;
}

export interface NudgePreview {
  preview: true;
  template_name: string;
  template_data: {
    recipientName: string | null;
    stepLabel: string;
    stepHeadline: string;
    stepDescription: string;
    benefit: string;
    daysStuck: number | null;
    ctaLabel: string;
    ctaHref: string;
    adminMessage: string | null;
    steps: Array<{ label: string; state: 'done' | 'current' | 'next' }>;
  };
}

interface NudgeParams {
  userId: string;
  funnelStep: string;
  customMessage?: string | null;
  daysStuck?: number | null;
  force?: boolean;
}

async function invokeNudge(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('send-activation-nudge', { body });
  if (error) {
    // Surface the real server error (invoke collapses non-2xx into a generic message).
    let details = error.message;
    try {
      const ctx = (error as any).context;
      if (ctx?.text) details = await ctx.text();
    } catch { /* keep default */ }
    throw new Error(details);
  }
  // Business-rule blocks come back as 200 { blocked: true, error: '...' }.
  if (data && (data as any).blocked) {
    throw new Error(JSON.stringify(data));
  }
  return data;
}


export function useNudgePreview(params: NudgeParams & { enabled?: boolean }) {
  return useQuery({
    queryKey: ['nudge-preview', params.userId, params.funnelStep, params.customMessage, params.daysStuck],
    enabled: !!params.userId && !!params.funnelStep && params.enabled !== false,
    staleTime: 0,
    queryFn: async (): Promise<NudgePreview> =>
      invokeNudge({
        user_id: params.userId,
        funnel_step: params.funnelStep,
        custom_message: params.customMessage ?? null,
        days_stuck: params.daysStuck ?? null,
        dry_run: true,
      }) as Promise<NudgePreview>,
  });
}

export function useSendActivationNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: NudgeParams) =>
      invokeNudge({
        user_id: params.userId,
        funnel_step: params.funnelStep,
        custom_message: params.customMessage ?? null,
        days_stuck: params.daysStuck ?? null,
        force: params.force ?? false,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user-360'] });
      qc.invalidateQueries({ queryKey: ['admin-user-search'] });
    },
  });
}


export function useAdminUserSearch(params: {
  query: string;
  segment: AdminUserSegment;
  periodDays: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['admin-user-search', params.query, params.segment, params.periodDays, params.limit ?? 50],
    queryFn: async (): Promise<AdminUserSearchData> => {
      const { data, error } = await supabase.functions.invoke('admin-user-360', {
        body: {
          action: 'search',
          query: params.query,
          segment: params.segment,
          period_days: params.periodDays,
          limit: params.limit ?? 50,
        },
      });
      if (error) throw error;
      return data as AdminUserSearchData;
    },
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  });
}

export interface BulkNudgeTarget {
  userId: string;
  email: string | null;
  name: string | null;
  funnelStep: string;
  daysStuck?: number | null;
}

export interface BulkNudgeResult {
  userId: string;
  email: string | null;
  name: string | null;
  ok: boolean;
  errorKey?: string;
}

/**
 * Sends the activation nudge one recipient at a time (each lead gets its own
 * transactional email, its own outreach row and its own click token).
 */
export function useBulkActivationNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      targets: BulkNudgeTarget[];
      customMessage?: string | null;
      force?: boolean;
      onProgress?: (done: number, total: number, last: BulkNudgeResult) => void;
    }): Promise<BulkNudgeResult[]> => {
      const results: BulkNudgeResult[] = [];
      for (let i = 0; i < params.targets.length; i++) {
        const t = params.targets[i];
        let res: BulkNudgeResult;
        try {
          await invokeNudge({
            user_id: t.userId,
            funnel_step: t.funnelStep,
            custom_message: params.customMessage ?? null,
            days_stuck: t.daysStuck ?? null,
            force: params.force ?? false,
          });
          res = { userId: t.userId, email: t.email, name: t.name, ok: true };
        } catch (e: any) {
          res = {
            userId: t.userId,
            email: t.email,
            name: t.name,
            ok: false,
            errorKey: parseErrorKey(String(e?.message ?? '')),
          };
        }
        results.push(res);
        params.onProgress?.(i + 1, params.targets.length, res);
        if (i < params.targets.length - 1) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }
      return results;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user-360'] });
      qc.invalidateQueries({ queryKey: ['admin-user-search'] });
    },
  });
}

export function parseErrorKey(message: string): string {
  try {
    return JSON.parse(message)?.error ?? 'unknown';
  } catch {
    return message.match(/"error"\s*:\s*"([a-z_]+)"/)?.[1] ?? 'unknown';
  }
}

export function useAdminUser360(userId: string | undefined, periodDays: number) {
  return useQuery({
    queryKey: ['admin-user-360', userId, periodDays],
    queryFn: async (): Promise<AdminUser360Data> => {
      const { data, error } = await supabase.functions.invoke('admin-user-360', {
        body: { action: 'detail', user_id: userId, period_days: periodDays },
      });
      if (error) throw error;
      return data as AdminUser360Data;
    },
    enabled: !!userId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}
